/**
 * Payment-link API client — the API behind the /pay page
 * (sw-ev-api docs/PAYMENT_RECOVERY.md).
 *
 * Deliberately NOT the default axios instance:
 *   - no cookies (withCredentials stays off) and no Bearer header: the link
 *     token is the only credential, and a signed-in driver's identity has
 *     no business on this request;
 *   - no telemetry interceptors: the journey telemetry reports the path of
 *     every failed default-axios request;
 *   - the token rides in a request header only. It is never part of a path
 *     or a query string.
 *
 * Nothing in this module writes to the browser log: an HTTP-client error
 * carries the request headers (the token) and the request body (the card).
 * Every failure is reduced to a short code before it leaves this module.
 */
import axios from 'axios';
import { getPayLinkToken } from '../pay/payLinkToken.js';

const BASE = `${String(import.meta.env.VITE_API_URL || '').replace(/\/+$/, '')}/pay-links`;

const client = axios.create({
  baseURL: BASE,
  withCredentials: false,
  // A pre-authorization waits on the card network.
  timeout: 60_000,
});

const CODE_SHAPE = /^[A-Za-z_]{2,40}$/;
const ATTEMPT_ID_SHAPE = /^[A-Za-z0-9-]{1,64}$/;
const LINK_STATES = ['not_found', 'expired', 'replaced'];
const CARD_FIELDS = ['number', 'expiry', 'cvv'];

/** The origin the 3DS result page posts its message from. */
export function payLinkApiOrigin() {
  try {
    return new URL(BASE, window.location.origin).origin;
  } catch {
    return null;
  }
}

/** Reduce any failure to a code and the few fields the page acts on. */
function failure(err) {
  const status = Number.isInteger(err?.response?.status) ? err.response.status : null;
  const body = err?.response?.data;
  const named = typeof body?.error === 'string' && CODE_SHAPE.test(body.error) ? body.error : null;
  let code = named;
  if (!code) {
    if (status == null) code = 'NETWORK';
    else if (status === 429) code = 'RATE_LIMITED';
    else if (status === 404) code = 'link_not_found';
    else code = 'SERVER';
  }
  const out = { ok: false, code, status };
  if (LINK_STATES.includes(body?.state)) out.linkState = body.state;
  else if (code === 'link_not_found' || code === 'NOT_FOUND') out.linkState = 'not_found';
  else if (code === 'link_expired') out.linkState = 'expired';
  else if (code === 'link_revoked') out.linkState = 'replaced';
  if (CARD_FIELDS.includes(body?.field)) out.field = body.field;
  if (body?.attemptId != null && ATTEMPT_ID_SHAPE.test(String(body.attemptId))) out.attemptId = String(body.attemptId);
  return out;
}

async function call(method, path, { data, headers } = {}) {
  const token = getPayLinkToken();
  if (!token) return { ok: false, code: 'NO_LINK', status: null };
  try {
    const res = await client.request({
      method,
      url: path,
      data,
      headers: { ...headers, 'X-Pay-Link-Token': token },
    });
    const body = res?.data;
    if (!body || typeof body !== 'object' || body.ok !== true) return { ok: false, code: 'SERVER', status: res?.status ?? null };
    return { ok: true, data: body };
  } catch (err) {
    return failure(err);
  }
}

const attemptPath = (attemptId) => {
  const id = String(attemptId ?? '');
  return ATTEMPT_ID_SHAPE.test(id) ? `/current/attempts/${id}` : null;
};

/** What the link is for and what there is to pay. */
export function getPayContext() {
  return call('get', '/current');
}

/**
 * Start a card authorization. `idempotencyKey` is one per submit: a repeat
 * of the same submit returns the same attempt.
 */
export function startPayAttempt({ card, idempotencyKey }) {
  return call('post', '/current/attempts', {
    data: { card: { number: card.number, expMonth: card.expMonth, expYear: card.expYear, cvv: card.cvv } },
    headers: { 'Idempotency-Key': idempotencyKey },
  });
}

/** After the 3DS method step. */
export function continuePayAttempt(attemptId) {
  const path = attemptPath(attemptId);
  if (!path) return Promise.resolve({ ok: false, code: 'SERVER', status: null });
  return call('post', `${path}/continue`);
}

/** The attempt's current outcome — the only truth about a payment. */
export function getPayAttempt(attemptId) {
  const path = attemptPath(attemptId);
  if (!path) return Promise.resolve({ ok: false, code: 'SERVER', status: null });
  return call('get', path);
}
